import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2 } from "lucide-react";

export const emptyShareCapital = () => ({
  incorporation_date: new Date().toISOString().slice(0, 10),
  authorised_capital: "", face_value: 10, paid_up_capital: "",
  shareholders: [{ name: "", pan: "", shares: "" }], money_received: true,
});

export const shareCapitalPayload = (v) => ({
  incorporation_date: v.incorporation_date,
  authorised_capital: v.authorised_capital === "" ? null : Number(v.authorised_capital),
  face_value: Number(v.face_value), paid_up_capital: Number(v.paid_up_capital),
  money_received: !!v.money_received,
  shareholders: v.shareholders.filter((s) => s.name.trim()).map((s) => ({
    name: s.name.trim(), pan: (s.pan || "").toUpperCase(), shares: Number(s.shares),
    amount: Number(s.shares) * Number(v.face_value),
  })),
});

const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function ShareCapitalForm({ value, onChange }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const setRow = (i, patch) => set({ shareholders: value.shareholders.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const total = value.shareholders.reduce((a, s) => a + Number(s.shares || 0) * Number(value.face_value || 0), 0);
  const mismatch = value.paid_up_capital !== "" && Math.abs(total - Number(value.paid_up_capital)) > 0.01;
  const lbl = "text-[10px] font-mono uppercase tracking-widest text-foreground/60";

  return (
    <div className="space-y-3" data-testid="share-capital-form">
      <div className="grid grid-cols-2 gap-3">
        <div><Label className={lbl}>Incorporation date</Label><Input type="date" value={value.incorporation_date} onChange={(e) => set({ incorporation_date: e.target.value })} className="rounded-sm mt-1" data-testid="sc-incorporation-date" /></div>
        <div><Label className={lbl}>Authorised capital (optional)</Label><Input type="number" value={value.authorised_capital} onChange={(e) => set({ authorised_capital: e.target.value })} className="rounded-sm mt-1" data-testid="sc-authorised-capital" /></div>
        <div><Label className={lbl}>Paid-up capital</Label><Input type="number" value={value.paid_up_capital} onChange={(e) => set({ paid_up_capital: e.target.value })} className="rounded-sm mt-1" data-testid="sc-paid-up-capital" /></div>
        <div><Label className={lbl}>Face value / share</Label><Input type="number" value={value.face_value} onChange={(e) => set({ face_value: e.target.value })} className="rounded-sm mt-1" data-testid="sc-face-value" /></div>
      </div>
      <div className="space-y-2">
        <div className={lbl}>Shareholders</div>
        {value.shareholders.map((s, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center border-b border-foreground/5 pb-2" data-testid={`sc-shareholder-row-${i}`}>
            <Input placeholder="Name" value={s.name} onChange={(e) => setRow(i, { name: e.target.value })} className="col-span-12 rounded-sm" data-testid={`sc-sh-name-${i}`} />
            <Input placeholder="PAN" value={s.pan} maxLength={10} onChange={(e) => setRow(i, { pan: e.target.value.toUpperCase() })} className="col-span-5 rounded-sm font-mono" data-testid={`sc-sh-pan-${i}`} />
            <Input placeholder="Shares" type="number" value={s.shares} onChange={(e) => setRow(i, { shares: e.target.value })} className="col-span-3 rounded-sm" data-testid={`sc-sh-shares-${i}`} />
            <div className="col-span-3 text-xs font-mono text-right" data-testid={`sc-sh-amount-${i}`}>{fmt(Number(s.shares || 0) * Number(value.face_value || 0))}</div>
            <Button type="button" size="icon" variant="ghost" className="col-span-1 justify-self-end" disabled={value.shareholders.length === 1}
              onClick={() => set({ shareholders: value.shareholders.filter((_, j) => j !== i) })} data-testid={`sc-sh-remove-${i}`}><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
        <Button type="button" size="sm" variant="outline" className="rounded-sm gap-1" onClick={() => set({ shareholders: [...value.shareholders, { name: "", pan: "", shares: "" }] })} data-testid="sc-add-shareholder">
          <Plus className="w-3.5 h-3.5" /> Add shareholder
        </Button>
      </div>
      <div className={`text-xs ${mismatch ? "text-red-500" : "text-muted-foreground"}`} data-testid="sc-total">
        Shareholders total {fmt(total)}{mismatch ? ` — must equal paid-up capital ${fmt(value.paid_up_capital)}` : ""}
      </div>
      <label className="flex items-center gap-2 text-xs">
        <input type="checkbox" checked={value.money_received} onChange={(e) => set({ money_received: e.target.checked })} data-testid="sc-money-received" />
        Money received in bank (also posts Bank A/c Dr → Share Application Money Control A/c Cr)
      </label>
      <div className="text-[11px] text-muted-foreground">Posts on the incorporation date: Share Application Money Control A/c Dr → Share Capital A/c Cr (one line per shareholder).</div>
    </div>
  );
}
